import { route } from '@/ziggy-shim';
import { api, apiConfig, CastValue } from './LaravelClient';

export default {
  async index(projectId: Id, data: ApiRequests['api.v1.claims.index']) {
    return await api(route('api.v1.claims.index', projectId), 'get', {
      data,
    });
  },

  async create(projectId: Id, data: ApiRequests['api.v1.claims.store']) {
    return await api(route('api.v1.claims.store', projectId), 'post', {
      data,
    });
  },

  async update(id: Id, data: ApiRequests['api.v1.claims.update']) {
    return await api(route('api.v1.claims.update', id), 'patch', {
      data,
    });
  },

  async delete(id: Id) {
    return await api(route('api.v1.claims.destroy', id), 'delete');
  },

  async show(id: Id) {
    return await api(route('api.v1.claims.show', id), 'get');
  },

  async send(id: Id, data: ApiRequests['api.v1.claims.send.draft']) {
    return await api(route('api.v1.claims.send.draft', id), 'post', {
      data,
    });
  },

  async requestApproval(id: Id, data: ApiRequests['api.v1.claims.request-approval'] = {}) {
    return await api(route('api.v1.claims.request-approval', id), 'post', {
      data,
    });
  },

  async export(id: Id, data: ApiRequests['api.v1.claims.lite.export']) {
    return await api(route('api.v1.claims.lite.export', id), 'post', {
      data,
      casts: {
        apply_signature: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        depth: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        compact: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async exportRetentionRelease(projectId: Id, data: ApiRequests['api.v1.projects.export-retention-release']) {
    return await api(route('api.v1.projects.export-retention-release', projectId), 'post', {
      data,
      casts: {
        apply_signature: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        depth: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async exportFinalAccount(projectId: Id) {
    return await api(route('api.v1.projects.export-final-account', projectId), 'post');
  },

  async sendContractor(id: Id, signedParams: unknown, data: ApiRequests['api.v1.claims.send.contractor']) {
    return await apiConfig(route('api.v1.claims.send.contractor', id), 'post', {
      params: signedParams,
      paramsSerializer: {
        serialize: (params) => {
          return new URLSearchParams(params).toString();
        },
      },
    }, {
      data,
    });
  },

  async sendPqs(id: Id, signedParams: unknown, data: ApiRequests['api.v1.claims.send.pqs']) {
    return await apiConfig(route('api.v1.claims.send.pqs', id), 'post', {
      params: signedParams,
      paramsSerializer: {
        serialize: (params) => {
          return new URLSearchParams(params).toString();
        },
      },
    }, {
      data,
    });
  },

  async submit(id: Id, signedParams: unknown, data: ApiRequests['api.v1.claims.certify']) {
    return await apiConfig(route('api.v1.claims.certify', id), 'post', {
      params: signedParams,
      paramsSerializer: {
        serialize: (params) => {
          return new URLSearchParams(params).toString();
        },
      },
    }, {
      data,
    });
  },

  async certifyLite(id: Id, data: ApiRequests['api.v1.claims.lite.certify']) {
    return await api(route('api.v1.claims.lite.certify', id), 'post', {
      data,
    });
  },

  async uncertify(id: Id) {
    return await api(route('api.v1.claims.lite.uncertify', id), 'post');
  },

  async storeMedia(id: Id, data: ApiRequests['api.v1.claims.store-media'] | FormData) {
    return await api(route('api.v1.claims.store-media', id), 'post', {
      data,
    });
  },

  async media(id: Id) {
    return await api(route('api.v1.claims.media', id), 'get');
  },

  async resendPqs(id: Id) {
    return await api(route('api.v1.claims.resend-pqs', id), 'post');
  },

  async resendContractor(id: Id) {
    return await api(route('api.v1.claims.resend-contractor', id), 'post');
  },

  admin: {
    async index(page = 1) {
      return await apiConfig(route('api.v1.admin.claims.index'), 'get', {
        params: {
          page,
        },
      });
    },

    async show(id: Id) {
      return await api(route('api.v1.admin.claims.show', id), 'get');
    },

    async update(id: Id, data: ApiRequests['api.v1.admin.claims.update']) {
      return await api(route('api.v1.admin.claims.update', id), 'patch', {
        data,
      });
    },

    async delete(id: Id) {
      return await api(route('api.v1.admin.claims.destroy', id), 'delete');
    },
  },

  subcontractor: {
    async dashboardIndex(projectId: Id, data: ApiRequests['api.v1.subcontractor.claims.dashboard.index']) {
      return await api(route('api.v1.subcontractor.claims.dashboard.index', projectId), 'get', {
        data,
        casts: {
          month: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async show(projectId: Id, claimId: Id) {
      return await api(route('api.v1.subcontractor.claims.show', [projectId, claimId]), 'get');
    },

    async summary(projectId: Id, claimId: Id) {
      return await api(route('api.v1.subcontractor.claims.summary', [projectId, claimId]), 'get');
    },

    async store(projectId: Id, projectSupplierId: Id, data: ApiRequests['api.v1.subcontractor.claims.store']) {
      return await api(route('api.v1.subcontractor.claims.store', [projectId, projectSupplierId]), 'post', {
        data,
      });
    },

    async issue(projectId: Id, claimId: Id, data: ApiRequests['api.v1.subcontractor.claims.issue']) {
      return await api(route('api.v1.subcontractor.claims.issue', [projectId, claimId]), 'post', {
        data,
      });
    },

    async issueCertificate(
      projectId: Id,
      claimId: Id,
      data: ApiRequests['api.v1.subcontractor.claims.issue-certificate'],
    ) {
      return await api(route('api.v1.subcontractor.claims.issue-certificate', [projectId, claimId]), 'post', {
        data,
        casts: {
          compact: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async massSend(projectId: Id, data: ApiRequests['api.v1.subcontractor.claims.mass-send']) {
      return await api(route('api.v1.subcontractor.claims.mass-send', projectId), 'post', {
        data,
      });
    },

    async submit(id: Id, signedParams: unknown, data: ApiRequests['api.v1.subcontractor.claims.submit']) {
      return await apiConfig(route('api.v1.subcontractor.claims.submit', id), 'post', {
        params: signedParams,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      }, {
        data,
      });
    },

    async updateSopaDisclaimer(
      id: Id,
      signedParams: unknown,
      data: ApiRequests['api.v1.subcontractor.claims.sopa-disclaimer.update'],
    ) {
      return await apiConfig(route('api.v1.subcontractor.claims.sopa-disclaimer.update', id), 'post', {
        params: signedParams,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      }, {
        data,
      });
    },

    async certify(projectId: Id, claimId: Id, data: ApiRequests['api.v1.subcontractor.claims.certify']) {
      return await api(route('api.v1.subcontractor.claims.certify', [projectId, claimId]), 'post', {
        data,
      });
    },

    async updateSopaDisclaimerLite(
      id: Id,
      data: ApiRequests['api.v1.subcontractor.claims.lite.sopa-disclaimer.update'],
    ) {
      return await api(route('api.v1.subcontractor.claims.lite.sopa-disclaimer.update', id), 'post', {
        data,
      });
    },

    async certifyLite(id: Id, data: ApiRequests['api.v1.subcontractor.claims.lite.certify']) {
      return await api(route('api.v1.subcontractor.claims.lite.certify', id), 'post', {
        data,
      });
    },

    async uncertify(projectId: Id, claimId: Id) {
      return await api(route('api.v1.subcontractor.claims.uncertify', [projectId, claimId]), 'post');
    },

    async uncertifyLite(id: Id) {
      return await api(route('api.v1.subcontractor.claims.lite.uncertify', id), 'post');
    },

    async storeMedia(
      id: Id,
      signedParams: unknown,
      data: ApiRequests['api.v1.subcontractor.claims.media.store'] | FormData,
    ) {
      return await apiConfig(route('api.v1.subcontractor.claims.media.store', id), 'post', {
        params: signedParams,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      }, {
        data,
      });
    },

    async media(id: Id, signedParams: unknown) {
      return await apiConfig(route('api.v1.subcontractor.claims.media', id), 'get', {
        params: signedParams,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      });
    },

    async destroyMedia(id: Id, signedParams: unknown, data: ApiRequests['api.v1.subcontractor.claims.media.destroy']) {
      return await apiConfig(route('api.v1.subcontractor.claims.media.destroy', id), 'delete', {
        params: signedParams,
        paramsSerializer: {
          serialize: (params) => {
            return new URLSearchParams(params).toString();
          },
        },
      }, {
        data,
      });
    },

    async export(projectId: Id, claimId: Id, data: ApiRequests['api.v1.subcontractor.claims.export']) {
      return await api(route('api.v1.subcontractor.claims.export', [projectId, claimId]), 'post', {
        data,
        casts: {
          apply_signature: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
          compact: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },
  },
};
